import { AggregateLocationViewModel } from '@caslink/common-client/src';
import { DashboardAreaSummary } from '../shared/models/dashboard-area-summary.interface';
import { DashboardService } from './dashboard.service';
import { Injectable } from '@angular/core';
import { ProxyService } from '@caslink/common-client/ng-app/proxy/ProxyService';

export interface DashboardCache {
    [locationId: number]: DashboardCacheProps;
}

interface DashboardCacheProps {
    locationId: number;
    locationName?: string;
    areas?: DashboardAreaSummary[],
}

@Injectable({
    providedIn: 'root',
})
export class DashboardCacheService {
    private readonly dashboards: DashboardCache = {};

    constructor(
        private dashboardService: DashboardService,
        private proxyService: ProxyService,
    ) {
    }

    setCachedDashboard(dashboard: DashboardCacheProps) {
        if (this.dashboards.hasOwnProperty(dashboard.locationId)) {
            this.dashboards[dashboard.locationId] = {
                ...this.dashboards[dashboard.locationId],
                ...dashboard,
            };
        } else {
            this.dashboards[dashboard.locationId] = { ...dashboard };
        }
    }

    async getDeviceAreaName(locationId: number, hardwareItemId: number) {
        const dashboard = await this.getCachedDashboard(locationId);

        if (!dashboard) {
            return;
        }

        for (const area of dashboard.areas) {
            const deviceName = this.findDeviceInArea(hardwareItemId, area);
            if (deviceName) {
                return deviceName;
            }
        }
    }

    async getLocationName(locationId: number) {
        return (await this.getCachedDashboard(locationId)).locationName;
    }

    private findDeviceInArea(hardwareItemId: number, area: DashboardAreaSummary) {
        for (const device of area.devices) {
            if (device.hardwareItem.id === hardwareItemId) {
                return area.areaTag;
            }
        }
    }

    private loadDashboardDevices(locationId: number) {
        return this.dashboardService.getDevices(locationId) as Promise<CASLink.Device.Ui.ViewModel.Dashboard.DashboardViewModel>;
    }

    private loadLocationDetails(locationId: number) {
        return this.proxyService.nativeRequest({
            endPoint: 'location', route: 'getAggregateLocation',
            routeParams: { locationId: locationId }
        }).as<AggregateLocationViewModel>();
    }

    private async getCachedDashboard(locationId: number) {
        const locationNameLoaded = this.dashboards[locationId]?.locationName;
        const locationDevicesLoaded = this.dashboards[locationId]?.areas;

        const resolveNull = () => Promise.resolve(null);

        const loadLocationDetails = locationNameLoaded ? resolveNull() : this.loadLocationDetails(locationId);
        const loadDashboardDevices = locationDevicesLoaded ? resolveNull() : this.loadDashboardDevices(locationId);

        const [location, devices] = await Promise.all([loadLocationDetails, loadDashboardDevices]);

        if (location) {
            this.setCachedDashboard({ locationName: location.description, locationId });
        }

        if (devices) {
            this.setCachedDashboard({ areas: devices.areas, locationId });
        }
        return this.dashboards[locationId];
    }
}
